import { useQuery } from '@tanstack/react-query';
import { VETTED_QUERY } from '../../services/talentCommunityApi';
import { Card, Status } from './ui';

/**
 * The project on one screen: the three groups, where each stands, what is built next and which
 * decisions are open. A starting point for the team's discussion, not a dashboard: no candidate
 * data is on this page yet. Follows design v0.6 and the open-items list (Anna, Oct 16 2026).
 * Owners are left out on purpose until the team has agreed them. The other tabs show the screens of
 * Anna's mockup v0.5, empty until real candidates arrive.
 */
const GROUPS = [
  {
    name: 'Vetted Talent',
    who: 'Candidates we interviewed and did not hire. A recruiter invites them from Lever; they join with their own consent.',
    status: <Status tone="next">Next: Lever sync</Status>,
  },
  {
    name: 'Leads',
    who: 'Sourced people who were never interviewed. They join by signing up themselves through a link.',
    status: <Status tone="preview">Sign-up live as a preview</Status>,
  },
  {
    name: 'Internal Talents',
    who: 'FINN employees open to another role. Their own manager never sees it.',
    status: <Status tone="later">Not in version 1</Status>,
  },
];

const JOIN_STEPS = [
  'The recruiter adds the Lever tag invite-to-talent-community and archives the opportunity. The archive reason stays as it is, so it still says why we did not hire them.',
  'The tool sends the invitation from the TA sender, with a personal link. Until the candidate answers it keeps only the Lever opportunity ID and the invitation date.',
  'The link opens a consent page with two unticked choices: stay in the Talent Community and hear about matching jobs, and, separately, receive news.',
  'Only after they agree is the profile created from Lever (CV, history, notes), and they get a confirmation email.',
  'No answer within 14 days: the link expires and the stub is deleted. No reminder.',
];

const STAY_RULES = [
  'Consent lasts 24 months. Renewal emails go out at month 22, month 23 and 7 days before it ends.',
  'A click in an email never changes anything by itself: the candidate decides in their portal.',
  'Opt out: their data is deleted within 24 hours. No answer: deleted on the day consent ends.',
  'Deleting keeps only a row with no personal data (group, source, campaign, dates, outcome), so the counts stay right.',
];

const BUILD_ORDER: [string, 'live' | 'preview' | 'next' | 'later'][] = [
  ['Leads sign-up with double opt-in, welcome email and the candidate’s own data page', 'preview'],
  ['Lever sync: a tagged, archived Vetted Talent candidate appears in the tool', 'next'],
  ['Consent and portal: invitation email, consent page, renewal emails, anonymizing', 'next'],
  ['Search and profiles on real data: filters, then search in plain words; select people into a draft campaign', 'later'],
  ['Campaigns: tied to one Lever job, sent from the recruiter’s Gmail, Interested creates the opportunity in Lever', 'later'],
  ['Dashboard on real data: groups, campaigns, demographics, GDPR', 'later'],
  ['Full test round with test candidates, then the checks before real candidates (DPIA, consent texts, legal)', 'later'],
  ['Internal Talents, after the works council', 'later'],
];

const LABEL = { live: 'Live', preview: 'Preview', next: 'Next', later: 'Later' } as const;

const DECISIONS = [
  'Consent texts: Vetted Talent, Leads sign-up and the renewal emails, for legal review',
  'Legal sign-off on Leads: approach first, store only once they opt in themselves',
  'Legal check of the invitation email (UWG §7) and the 14 days before consent',
  'DPIA before go-live, and the EU provider for search in plain words',
  'Lever: create the tag invite-to-talent-community; check our key can create opportunities',
  'A dedicated TA sender for automated emails',
  'Email hero images: where they are hosted',
];

export function OverviewTab() {
  const vetted = useQuery(VETTED_QUERY);
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        {GROUPS.map((g) => (
          <Card key={g.name} title={g.name} aside={g.status}>
            <p className="mb-4 text-sm text-zinc-600">{g.who}</p>
            <p className="text-xs text-zinc-500">People in the group</p>
            {g.name === 'Vetted Talent' && vetted.data ? (
              <p className="text-2xl font-semibold text-zinc-900">{vetted.data.length}</p>
            ) : (
              <p className="text-2xl font-semibold text-zinc-400">—</p>
            )}
          </Card>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="How Vetted Talent join">
          <ol className="list-decimal space-y-2 pl-5 text-sm text-zinc-700">
            {JOIN_STEPS.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-zinc-500">How Leads join is on the Leads sign-up tab.</p>
        </Card>
        <Card title="Staying in, and leaving">
          <ul className="list-disc space-y-2 pl-5 text-sm text-zinc-700">
            {STAY_RULES.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="Build order">
          <ol className="space-y-2">
            {BUILD_ORDER.map(([what, tone], i) => (
              <li key={what} className="flex items-start gap-3 text-sm">
                <span className="w-5 shrink-0 text-right text-zinc-400">{i + 1}.</span>
                <span className="flex-1 text-zinc-800">{what}</span>
                <Status tone={tone}>{LABEL[tone]}</Status>
              </li>
            ))}
          </ol>
        </Card>

        <Card title="Decisions open">
          <ul className="list-disc space-y-2 pl-5 text-sm text-zinc-800">
            {DECISIONS.map((what) => (
              <li key={what}>{what}</li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
